import { Debounce } from "@/components/Debounce";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useQueryData from "@/hooks/useQueryData";
import { cn } from "@/lib/utils";
import { SuccessFetchListResponse } from "@/services/api";
import {
  keepPreviousData,
  QueryKey,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, XIcon } from "lucide-react";
import {
  ChangeEventHandler,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface Option {
  [key: string]: unknown;
  id: string;
  name?: string;
}

export type AsyncSelectLoading = "lazy" | "eager";

export interface AsyncSelectProps<T extends Option>
  extends ControllerRenderProps {
  loadingLabel?: string;
  searchLabel?: string;
  noResultsLabel?: string;
  placeholder?: string;
  queryKey: QueryKey;
  queryFn: () => Promise<SuccessFetchListResponse<T>>;
  /** `"lazy"` (default): fetch when the dropdown opens. `"eager"`: fetch on mount. */
  loading?: AsyncSelectLoading;
  /** Controls whether data fetching is allowed for the current state. */
  loadingCondition?: boolean;
  enableFilter?: boolean;
  className?: string;
  onFilter?: (value: string) => void;
  inDialog?: boolean;
  clear?: boolean;
  allowClearSelection?: boolean;
  clearLabel?: string;
  renderName?: (item: T) => React.ReactNode;
  defaultItem?: Partial<T>;
  footer?: (
    query: UseQueryResult<SuccessFetchListResponse<Option>, Error>,
  ) => ReactNode;
}

/** Base props for thin wrappers around `AsyncSelect`. Extends `ControllerRenderProps` with common passthrough props like `loading`. */
export interface AsyncSelectBaseProps extends ControllerRenderProps {
  loading?: AsyncSelectLoading;
  loadingCondition?: boolean;
}

/** @deprecated Use `AsyncSelectBaseProps` instead. */
export type AsyncSelectLoadingPassthrough = Pick<
  AsyncSelectProps<Option>,
  "loading"
>;

interface WithForwardRefType
  extends React.ForwardRefExoticComponent<
    Omit<AsyncSelectProps<Option>, "ref"> &
      React.RefAttributes<HTMLButtonElement>
  > {
  <T extends Option>(
    props: AsyncSelectProps<T> & React.RefAttributes<HTMLButtonElement>,
  ): ReturnType<
    React.FC<AsyncSelectProps<T> & React.RefAttributes<HTMLButtonElement>>
  >;
}

const AsyncSelect: WithForwardRefType = forwardRef<
  HTMLButtonElement,
  AsyncSelectProps<Option>
>(
  (
    {
      onFilter,
      onChange,
      value,
      loadingLabel,
      searchLabel,
      noResultsLabel,
      placeholder,
      queryKey,
      queryFn,
      loading = "lazy",
      loadingCondition = true,
      enableFilter = true,
      className,
      inDialog,
      clear,
      allowClearSelection = false,
      clearLabel,
      renderName,
      defaultItem: defaultItemProp,
      footer,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [filterInputValue, setFilterInputValue] = useState("");
    const shouldFetch = loadingCondition && (loading === "eager" || open);
    const query = useQuery({
      queryKey,
      queryFn,
      enabled: shouldFetch,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      placeholderData: keepPreviousData,
    });

    const { isFetching } = query;
    const queryData = useQueryData(query);
    const data = queryData;

    const selectedId = useRef(value);

    const [selectedItem, setSelectedItem] = useState<Option | undefined>(
      defaultItemProp?.name ? (defaultItemProp as Option) : undefined,
    );

    const disabled = props.disabled;

    useEffect(() => {
      selectedId.current = value;

      if (!value) {
        setSelectedItem(undefined);
        return;
      }

      const itemFromQuery = data?.find((item) => item.id === value);
      if (itemFromQuery) {
        setSelectedItem(itemFromQuery);
        return;
      }

      if (defaultItemProp?.id) {
        setSelectedItem(defaultItemProp as Option);
      }
    }, [value, defaultItemProp, data]);

    const onValueChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        const value = event.target.value;
        setFilterInputValue(value);
        onFilter?.(value);
      },
      [onFilter],
    );

    const onSelect = useCallback(
      (value: string) => {
        const found = data?.find((item) => item.id === value);
        setSelectedItem(found);
        selectedId.current = value;
        if (found) {
          onChange(value, found);
        } else {
          onChange(undefined);
        }
        setFilterInputValue("");
        setOpen(false);
      },
      [onChange, data],
    );

    const onClearSelection = useCallback(() => {
      // Pass empty string to ensure React Hook Form detects change and schema transforms it
      onChange("");
      setFilterInputValue("");
      setOpen(false);
    }, [onChange]);

    const onOpenChange = useCallback(
      (isOpen: boolean) => {
        setOpen(isOpen);
        if (isOpen) {
          onFilter?.("");
          setFilterInputValue("");
        }
      },
      [onFilter],
    );

    const onClear = useCallback(() => {
      onFilter?.("");
      setFilterInputValue("");
    }, [onFilter]);

    const selectedName = useMemo(() => {
      if (renderName && selectedItem) {
        return renderName(selectedItem);
      }
      return selectedItem?.name;
    }, [renderName, selectedItem]);

    const buttonLabel = selectedName;

    return (
      <>
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild {...props} ref={ref} className={className}>
            <Button
              variant="outline"
              className="justify-between"
              disabled={disabled}
            >
              <span className="grid grid-cols-[auto_1fr] items-center flex-grow">
                {buttonLabel ? (
                  <span className="block truncate">{buttonLabel}</span>
                ) : (
                  <span className="block truncate text-slate-500">
                    {placeholder}
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 justify-self-end" />
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn("w-[--radix-popover-trigger-width] p-0", {
              "pointer-events-auto": inDialog,
            })}
            onWheel={inDialog ? (e) => e.stopPropagation() : undefined}
          >
            <Command shouldFilter={false}>
              {enableFilter && (
                <Debounce onDebouncedChange={onValueChange}>
                  <CommandInput
                    value={filterInputValue}
                    placeholder={searchLabel}
                    isFetching={isFetching}
                    onClear={onClear}
                    {...props}
                  />
                </Debounce>
              )}
              <CommandList>
                <CommandEmpty>
                  {isFetching ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {loadingLabel && (
                        <span className="text-slate-500">{loadingLabel}</span>
                      )}
                    </span>
                  ) : (
                    noResultsLabel ?? t("common.noResults")
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {data?.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={onSelect}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedId.current === item.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      {renderName ? renderName(item) : item.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {allowClearSelection && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem value="" onSelect={onClearSelection}>
                        <XIcon className="mr-2 h-4 w-4" />
                        {clearLabel || t("asyncSelect.clear")}
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {footer?.(query)}
      </>
    );
  },
);

export default AsyncSelect;
